import { Direction } from '@angular/cdk/bidi';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { map, Observable, of, tap } from 'rxjs';
import { DateTime } from 'src/app/@shared/interfaces/date-time.interface';
import { SelectOption } from 'src/app/@shared/interfaces/select-option.interface';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { QueryParamsService } from 'src/app/@shared/services/query-params/query-params.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import {
  eDepartureTimeOptions,
  DEPARTURE_TIME_OPTIONS,
} from 'src/app/modules/route-planner/constants/departure-time-options.constants';
import {
  ARRIVAL_AT,
  DATE,
  DEPARTURE_AT,
  GO_NOW,
  TIME,
  TRIP_TIME_MODE_RADIO_GROUP_LABEL,
  TYPE_TIME_24_HOURS,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';

@Component({
    selector: 'mot-date-time-picking-form',
    templateUrl: './mot-date-time-picking-form.component.html',
    styleUrls: ['./mot-date-time-picking-form.component.scss'],
    standalone: false
})
export class MotDateTimePickingFormComponent implements OnInit {
  @Input() arrivalFormGroup: FormGroup;
  @Input() departureFormGroup: FormGroup;
  @Input() timeOptFormControl: FormControl;

  today = new Date();
  maxDate = new Date(new Date().setDate(new Date().getDate() + 8));
  eTimeOptions = eDepartureTimeOptions;
  departureTimeOpts$: Observable<SelectOption<eDepartureTimeOptions>[]>

  dateText: string
  timeText: string

  direction: Direction
  typeTime24HoursText: string
  tripTimeModeGroupLabel = ''

  constructor(
    private readonly routePlanningService: RoutePlanningService,
    private readonly queryParamsService: QueryParamsService,
    private readonly languageService: LanguageService,
    private readonly resizeService: ResizeService
  ) { }

  ngOnInit(): void {
    const params = this.queryParamsService.currentParams;

    this.initializeTimeOptionControl(params['timeType']);
    this.initializeTimeForm(this.arrivalFormGroup, parseInt(params['time']));
    this.initializeTimeForm(this.departureFormGroup, parseInt(params['time']));
    this.initializeMultiLanguageTexts()
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe(language => {
      this.direction = language.direction
      this.dateText = DATE[language.language]
      this.timeText = TIME[language.language]
      this.typeTime24HoursText = TYPE_TIME_24_HOURS[language.language]
      this.tripTimeModeGroupLabel =
        TRIP_TIME_MODE_RADIO_GROUP_LABEL[language.language]
      const timeOptArray = [...DEPARTURE_TIME_OPTIONS]
      timeOptArray.forEach(timeOpt => {
        switch (timeOpt.value) {
          case eDepartureTimeOptions.NOW:
            timeOpt.label = GO_NOW[language.language]
            break
          case eDepartureTimeOptions.ARRIVAL:
            timeOpt.label = ARRIVAL_AT[language.language]
            break
          case eDepartureTimeOptions.DEPART:
            timeOpt.label = DEPARTURE_AT[language.language]
            break
          default:
            timeOpt.label = GO_NOW[language.language]
        }
      })
      this.departureTimeOpts$ = of(timeOptArray)
    })
  }

  initializeTimeForm(formGroup: FormGroup<any>, time: number | null) {
    if (!!time) {
      const timeAsDate = new Date(time);
      formGroup.setValue({
        date: timeAsDate,
        time: this.routePlanningService.calcDateHour(timeAsDate),
      });
    }

    formGroup.valueChanges.subscribe(() => {
      const updatedTime = this.updateDateTime(formGroup);

      const miliseconds: number = updatedTime.getTime();
      this.queryParamsService.setQueryParam('time', miliseconds.toString());
    });
  }

  initializeTimeOptionControl(timeType: string | null) {
    if (!!timeType) {
      this.timeOptFormControl.setValue(eDepartureTimeOptions[timeType]);
    }

    this.timeOptFormControl.valueChanges.subscribe((type) => {
      if (type !== eDepartureTimeOptions.NOW) {
        this.queryParamsService.setQueryParam(
          'timeType',
          eDepartureTimeOptions[type]
        );
      } else {
        this.queryParamsService.setQueryParam('timeType', null);
        this.queryParamsService.setQueryParam('time', null);
      }
    });
  }

  updateDateTime(formGroup: FormGroup): Date {
    const date = formGroup.get('date')?.value as Date;
    const time = formGroup.get('time')?.value as string;

    if (date && time) {
      const [hours, minutes] = time.split(':').map((num) => parseInt(num, 10));
      date.setHours(hours, minutes);
    }

    return date;
  }

  get isDesktop(): boolean {
    return this.resizeService.isDesktop
  }
}
